import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import DragIndicatorIcon from '@material-design-icons/svg/filled/drag_indicator.svg?react';
import type { DragEvent } from 'react';

import { S3Image } from '../../../components/S3Image';
import { cn } from '../../../lib/utils';
import type {
  LoadDappReviews,
  SpotlightAppEntry,
  SpotlightFeaturedReview,
} from '../types';
import { FeaturedReviewEditor } from './FeaturedReviewEditor';

interface AppEntryRowProps {
  entry: SpotlightAppEntry;
  position: number;
  dappName: string;
  dappId: string | null;
  iconUrl: string | null;
  readOnly: boolean;
  isDragging: boolean;
  isDragOver: boolean;
  loadReviews: LoadDappReviews;
  onDragStart: (event: DragEvent<HTMLLIElement>) => void;
  onDragOver: (event: DragEvent<HTMLLIElement>) => void;
  onDragLeave: () => void;
  onDrop: (event: DragEvent<HTMLLIElement>) => void;
  onDragEnd: () => void;
  onCurationNoteChange: (value: string) => void;
  onFeaturedReviewChange: (review: SpotlightFeaturedReview | null) => void;
  onRemove: () => void;
}

export function AppEntryRow({
  entry,
  position,
  dappName,
  dappId,
  iconUrl,
  readOnly,
  isDragging,
  isDragOver,
  loadReviews,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onCurationNoteChange,
  onFeaturedReviewChange,
  onRemove,
}: AppEntryRowProps) {
  return (
    <li
      draggable={!readOnly}
      onDragStart={readOnly ? undefined : onDragStart}
      onDragOver={readOnly ? undefined : onDragOver}
      onDragLeave={readOnly ? undefined : onDragLeave}
      onDrop={readOnly ? undefined : onDrop}
      onDragEnd={readOnly ? undefined : onDragEnd}
      className={cn(
        'rounded-xl border bg-neutral-900/50 p-3 transition-colors',
        isDragOver
          ? 'border-teal-500/60 bg-neutral-900/80'
          : 'border-neutral-800',
        isDragging && 'opacity-50',
      )}
    >
      <div className="flex items-start gap-3">
        {readOnly ? (
          <span className="flex size-6 items-center justify-center rounded-md bg-neutral-800 text-[11px] font-semibold tabular-nums text-neutral-400">
            {position}
          </span>
        ) : (
          <span
            aria-label="Drag to reorder"
            className="flex h-6 w-5 cursor-grab items-center justify-center text-neutral-500 hover:text-neutral-300 active:cursor-grabbing"
          >
            <DragIndicatorIcon className="size-5 fill-current" />
          </span>
        )}

        <S3Image
          src={iconUrl}
          alt=""
          aria-hidden="true"
          className="size-9 shrink-0 rounded-lg object-cover ring-1 ring-white/10"
          fallback={
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-neutral-800/60 ring-1 ring-white/10">
              <AppsIcon className="size-4 fill-current text-neutral-500" />
            </div>
          }
        />

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-white">
                {dappName}
              </div>
              <div className="truncate font-mono text-xs text-neutral-500">
                {entry.androidPackage}
              </div>
            </div>
            {!readOnly && (
              <button
                type="button"
                aria-label={`Remove ${entry.androidPackage}`}
                onClick={onRemove}
                className="flex size-7 shrink-0 items-center justify-center rounded-lg text-neutral-400 hover:bg-neutral-800 hover:text-white"
              >
                <CloseIcon className="size-4 fill-current" />
              </button>
            )}
          </div>

          <input
            aria-label={`Curation note for ${entry.androidPackage}`}
            value={entry.curationNote ?? ''}
            disabled={readOnly}
            onChange={(event) => onCurationNoteChange(event.target.value)}
            placeholder="Curation note (shown with the app)"
            className="h-9 w-full rounded-lg border border-neutral-800 bg-neutral-900/70 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-teal-500/60 focus:outline-none disabled:opacity-60"
          />

          <FeaturedReviewEditor
            review={entry.featuredReview ?? null}
            onChange={onFeaturedReviewChange}
            disabled={readOnly}
            dappId={dappId}
            appName={dappName}
            loadReviews={loadReviews}
          />
        </div>
      </div>
    </li>
  );
}
